import { Box, Stack, Typography } from "@mui/material";
import { WarningAmberRounded } from "@mui/icons-material";
import AcxButton from "components/UI/AcxButton";
import AcxDialog from "components/UI/Dialog/AcxDialog";
import { useStore } from "utils/useStore";
import { LLMClassifierBuilderStore } from "../Stores/LLMClassifierBuilderStore";
import { llmClassifierVersionLabel } from "../utils/llmClassifierVersionDisplay";

interface UnsavedChangesDialogProps {
    isOpen: boolean;
    isDiscarding?: boolean;
    /** Set only when this guards switching to a specific version, rather than leaving the page. */
    targetVersionLabel?: string;
    onClose: () => void;
    onDiscard: () => void;
    onSaveFirst: () => void;
}

const boldVersion = (label: string) => (
    <Box component="span" fontWeight="bold">
        {label}
    </Box>
);

export function UnsavedChangesDialog(props: UnsavedChangesDialogProps) {
    const store = useStore(LLMClassifierBuilderStore);
    const editingVersionLabel = llmClassifierVersionLabel(
        store.classifier.implementation.createdOn,
    );
    const changes =
        store.getSaveChanges()?.sections.flatMap((s) => s.changes) ?? [];

    return (
        <AcxDialog
            isOpen={props.isOpen}
            onClose={props.onClose}
            buttonsDisabled={props.isDiscarding}
            hideCancelButton
            title={
                <Stack direction="row" alignItems="center" gap={1}>
                    <WarningAmberRounded sx={{ color: "yellow.main" }} />
                    You have unsaved changes
                </Stack>
            }
            dialogContentChildren={
                <>
                    <Typography variant="body2">
                        {props.targetVersionLabel ? (
                            <>
                                Opening {boldVersion(props.targetVersionLabel)}{" "}
                                will discard your unsaved edits
                            </>
                        ) : (
                            <>
                                Leaving this page will discard your unsaved
                                edits
                            </>
                        )}
                        {editingVersionLabel && (
                            <> on {boldVersion(editingVersionLabel)}</>
                        )}
                        . They have not been saved to any version and cannot be
                        recovered.
                    </Typography>
                    {changes.length > 0 && (
                        <Box
                            sx={(theme) => ({
                                border: `1px solid ${theme.palette.yellow.main}`,
                                borderRadius: 1,
                                backgroundColor:
                                    theme.palette.yellow.background,
                                p: 2,
                                my: 2,
                            })}
                        >
                            <Stack gap={1}>
                                {changes.map((change, index) => (
                                    <Stack
                                        key={`${index}-${change}`}
                                        direction="row"
                                        gap={1}
                                    >
                                        <Box
                                            component="span"
                                            sx={{ color: "yellow.main" }}
                                        >
                                            •
                                        </Box>
                                        <Typography variant="body2">
                                            {change}
                                        </Typography>
                                    </Stack>
                                ))}
                            </Stack>
                        </Box>
                    )}
                    {editingVersionLabel && (
                        <Typography variant="body2" color="gray.600">
                            {boldVersion(editingVersionLabel)} itself is
                            unaffected. Only the unsaved edits sitting on top of
                            it are lost.
                        </Typography>
                    )}
                </>
            }
        >
            <Stack direction="row" justifyContent="space-between" width="100%">
                <AcxButton color="white" onClick={props.onSaveFirst}>
                    Save Model first
                </AcxButton>
                <Stack direction="row" gap={1}>
                    <AcxButton color="white" onClick={props.onClose}>
                        Cancel
                    </AcxButton>
                    <AcxButton
                        color="error"
                        loading={props.isDiscarding}
                        onClick={props.onDiscard}
                    >
                        {props.targetVersionLabel
                            ? "Discard and switch"
                            : "Discard and leave"}
                    </AcxButton>
                </Stack>
            </Stack>
        </AcxDialog>
    );
}
